/* FG Store features block ("delivery first"): opens each row's popup (native <dialog>),
   keeps the free-delivery progress in step with the cart through Hyper's pubsub, and swaps
   the delivery icon for a check once the order qualifies.
   Progressive enhancement: without this script the card still renders its server state. */
(function () {
  if (customElements.get('fg-features')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class FgFeatures extends HTMLElement {
    connectedCallback() {
      this.trigger = null;
      this.animated = this.classList.contains('fg-features--motion') && !reducedMotion();
      this.onClick = this.onClick.bind(this);
      this.onCartEvent = (event) => this.refreshCart(event.detail);
      this.addEventListener('click', this.onClick);

      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close(dialog);
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) this.close(dialog);
        });
      });

      const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
      if (pubsub && pubsub.subscribe && pubsub.PUB_SUB_EVENTS) {
        this.unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, (payload) => this.refreshCart(payload));
        // trust panel: free delivery also depends on the selected size's price
        if (this.hasAttribute('data-trust') && pubsub.PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariant = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, (event) => this.onVariant(event));
        }
      }
      document.addEventListener('cart:updated', this.onCartEvent);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      document.removeEventListener('cart:updated', this.onCartEvent);
      document.documentElement.classList.remove('fg-features-lock');
    }

    onClick(event) {
      const closeButton = event.target.closest('[data-fgf-close]');
      if (closeButton) {
        this.close(closeButton.closest('dialog'));
        return;
      }
      const opener = event.target.closest('[data-fgf-open]');
      if (!opener) return;
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog) this.open(dialog, opener);
    }

    open(dialog, trigger) {
      if (dialog.open) return;
      this.trigger = trigger;
      dialog.showModal();
      document.documentElement.classList.add('fg-features-lock');

      const fill = dialog.querySelector('[data-fgf-fill]');
      const target = fill ? fill.style.transform : '';
      if (fill && this.animated) {
        fill.style.transform = 'scaleX(0)';
        void fill.offsetWidth;
      }
      if (this.animated) void dialog.offsetWidth;
      dialog.classList.add('is-open');
      if (fill && this.animated) fill.style.transform = target;

      if (fill) this.refreshCart();
    }

    close(dialog) {
      if (!dialog || !dialog.open || dialog.dataset.closing) return;
      dialog.classList.remove('is-open');
      const finish = () => {
        delete dialog.dataset.closing;
        dialog.close();
        document.documentElement.classList.remove('fg-features-lock');
        if (this.trigger) this.trigger.focus({ preventScroll: true });
        this.trigger = null;
      };
      if (!this.animated) {
        finish();
        return;
      }
      dialog.dataset.closing = 'true';
      setTimeout(finish, 380);
    }

    refreshCart(payload) {
      if (!this.querySelector('[data-fgf-fill]') && !this.hasAttribute('data-trust')) return;
      const cart = payload && payload.cart;
      if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
        this.update(cart.total_price);
        return;
      }
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(() => {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            if (data && typeof data.total_price === 'number') this.update(data.total_price);
          })
          .catch(() => {});
      }, 150);
    }

    update(totalCents) {
      if (this.hasAttribute('data-trust')) {
        this.updateTrust(totalCents);
        return;
      }
      const threshold = Number(this.dataset.threshold) || 0;
      if (!threshold) return;
      const d = this.dataset;
      const money = (cents) => this.formatMoney(cents);
      const bold = (template, token, value) => template.split(token).join(`<b>${escapeHtml(value)}</b>`);
      const plain = (template, values) =>
        Object.keys(values).reduce((text, token) => text.split(token).join(values[token]), template || '');

      let titleHtml;
      let spoken;
      let left;
      let right;
      let scale;
      let qualified = false;

      if (totalCents <= 0) {
        titleHtml = bold(escapeHtml(d.msgEmpty), '[threshold]', money(threshold));
        spoken = plain(d.msgEmpty, { '[threshold]': money(threshold) });
        left = d.capEmpty || '';
        right = money(threshold);
        scale = 0;
      } else if (totalCents < threshold) {
        const remaining = money(threshold - totalCents);
        titleHtml = bold(bold(escapeHtml(d.msgBelow), '[remaining]', remaining), '[threshold]', money(threshold));
        spoken = plain(d.msgBelow, { '[remaining]': remaining, '[threshold]': money(threshold) });
        left = plain(d.capProgress, { '[total]': money(totalCents), '[threshold]': money(threshold) });
        right = money(threshold);
        scale = Math.round((totalCents / threshold) * 1000) / 1000;
      } else {
        qualified = true;
        titleHtml = escapeHtml(plain(d.msgReached, { '[threshold]': money(threshold) }));
        spoken = plain(d.msgReached, { '[threshold]': money(threshold) });
        left = plain(d.capReached, { '[total]': money(totalCents), '[threshold]': money(threshold) });
        right = d.capReachedRight || '';
        scale = 1;
      }

      const live = this.querySelector('[data-fgf-live]');
      const changed = !this.lastSpoken || this.lastSpoken !== spoken;

      this.classList.toggle('is-qualified', qualified);
      this.querySelectorAll('[data-fgf-title]').forEach((node) => {
        if (node.innerHTML !== titleHtml) node.innerHTML = titleHtml;
      });
      this.querySelectorAll('[data-fgf-cap-left]').forEach((node) => (node.textContent = left));
      this.querySelectorAll('[data-fgf-cap-right]').forEach((node) => (node.textContent = right));
      this.querySelectorAll('[data-fgf-fill]').forEach((node) => (node.style.transform = `scaleX(${scale})`));

      if (live && changed && this.lastSpoken !== undefined) live.textContent = spoken;
      this.lastSpoken = spoken;
    }

    onVariant(event) {
      const data = event && event.data;
      if (!data || !data.variant || data.sectionId !== this.dataset.section) return;
      if (typeof data.variant.price === 'number') this.dataset.price = data.variant.price;
      this.updateTrust();
    }

    // Two states only (Danial 2026-09-22): this size's price OR the cart reaches the threshold.
    updateTrust(cartCents) {
      const threshold = Number(this.dataset.threshold) || 0;
      if (!threshold) return;
      if (typeof cartCents === 'number') this.dataset.cart = cartCents;
      const free = Number(this.dataset.price) >= threshold || Number(this.dataset.cart) >= threshold;
      if (this.classList.contains('is-free') === free) return;
      this.classList.toggle('is-free', free);
      const live = this.querySelector('[data-fgt-live]');
      const state = this.querySelector(free ? '.fg-trust__yes' : '.fg-trust__no');
      if (live && state) live.textContent = `Free delivery ${state.textContent}`;
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const value = cents / 100;
      const group = (amount, decimals, thousands, decimal) => {
        const [whole, fraction] = amount.toFixed(decimals).split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      };
      if (!match) return group(value, 0, ',', '.');
      let amount;
      switch (match[1]) {
        case 'amount_no_decimals':
          amount = group(value, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          amount = group(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          amount = group(value, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          amount = group(value, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          amount = group(value, 0, ' ', ',');
          break;
        case 'amount_with_apostrophe_separator':
          amount = group(value, 2, "'", '.');
          break;
        default:
          amount = group(value, 2, ',', '.');
      }
      return format.replace(match[0], amount.replace(/[.,]00$/, ''));
    }
  }

  customElements.define('fg-features', FgFeatures);
})();
